import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, computed, onMounted } from "vue";
import { VForm, VRow, VCol } from "vuetify/lib";
import { VacationFlowDat } from "@/models/transaction/VacationFlowDat";
import { VacationApplicationRequest } from "@/models/petition/PetitionRequest";
import { PetitionShiftSearch } from "@/models/petition/PetitionResponse";
import { VacationDaysDatKey } from "@/models/transaction/VacationDaysDatKey";
import { DateTime } from "luxon";
import VacationForm, { FormType } from "@/components/form/VacationForm.vue";
import { $alert } from "@/plugins/alert/alert";
import { $post } from "@/plugins/api/api";
import { Paths } from "@/plugins/api/paths";
import SubstituteHolidayFrom, {
  Item
} from "@/components/form/SubstituteHolidayFrom.vue";
import { ShiftPattern } from "@/models/transaction/ShiftPattern";
import { PetitionShiftPatternSearchRequest } from "@/models/petition/PetitionRequest";
import { ShiftFlowDat } from "@/models/transaction/ShiftFlowDat";
import BeforeAfterList from "@/components/list/BeforeAfterList.vue";
import { InputRules } from "@/rules";

interface VForm extends Vue {
  reset(): void;
  resetValidation(): void;
  validate(): boolean;
}

type Props = {
  date: DateTime;
  shift?: PetitionShiftSearch;
  affiliationId: number;
  employeeId: number;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'VacationApplication',
  props: {
    date: null,
    shift: null,
    affiliationId: null,
    employeeId: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
  date: DateTime;
  shift?: PetitionShiftSearch;
  affiliationId: number;
  employeeId: number;
};

const segmentItems = [
  { text: "休暇申請", value: "1" },
  { text: "振替申請", value: "0" }
] as const;

const beforeHeader = [
  { text: "シフト名： ", value: "preShiftpatternNm" },
  { text: "シフト開始時間： ", value: "preStartTime" },
  { text: "シフト終了時間： ", value: "preEndTime" },
  { text: "シフト総休憩時間： ", value: "preBreakTime" },
  { text: "シフト早朝休憩時間： ", value: "preMorningBreakTime" },
  { text: "シフト深夜休憩時間： ", value: "preMidnightBreakTime" }
] as const;

const afterHeader = [
  { text: "シフト名： ", value: "afterShiftpatternNm" },
  { text: "シフト開始時間： ", value: "afterStartTime" },
  { text: "シフト終了時間： ", value: "afterEndTime" },
  { text: "シフト総休憩時間： ", value: "afterBreakTime" },
  { text: "シフト早朝休憩時間： ", value: "afterMorningBreakTime" },
  { text: "シフト深夜休憩時間： ", value: "afterMidnightBreakTime" }
] as const;

const sqlToFormatTime = (time: string) => {
  const date = DateTime.fromISO(time);
  return date.isValid ? date.toFormat("HH:mm") : null;
};

const segment = ref(0);



const form = ref<FormType>({});
const formShift = ref(
  new ShiftFlowDat({
    recordId: 0,
    requestDate: DateTime.local().toISO(),
    date: props.date.toISO(),
    employeeId: props.employeeId,
    affiliationId: props.affiliationId,
    shiftId: props.shift?.shiftId,
    preShiftpatternId: props.shift?.shiftPatternId,
    preShiftpatternNm: props.shift?.shiftPatternName,
    preStartTime: props.shift?.shiftPatternStartTime,
    preEndTime: props.shift?.shiftPatternEndTime,
    preMorningBreakTime: props.shift?.shiftPatternMorningBreakTime,
    preBreakTime: props.shift?.shiftPatternBreakTime,
    preMidnightBreakTime: props.shift?.shiftPatternMidnightBreakTime
  })
);

const substitute = ref<Item>();

const vform = ref<VForm>();
const shiftPattern = ref<ShiftPattern[]>([]);
const selectShift = ref<ShiftPattern>();
const newShiftPattern = computed(() => [...shiftPattern.value]);

const formatDate = (date?: string) => {
  if (date)
    return DateTime.fromSQL(date)
      .setLocale("ja")
      .toISO();
};

watch(selectShift, (newValue?: ShiftPattern) => {
  console.log("newValue", newValue);
  const value = formShift.value;
  if (newValue) {
    value.afterShiftpatternId = newValue.shiftPatternId;
    value.afterShiftpatternNm = newValue.shiftPatternName;
    value.afterStartTime = sqlToFormatTime(newValue.startTime);
    value.afterEndTime = sqlToFormatTime(newValue.endTime);
    value.afterBreakTime = sqlToFormatTime(newValue.breakTime);
    value.afterMorningBreakTime = sqlToFormatTime(newValue.morningBreakTime);
    value.afterMidnightBreakTime = sqlToFormatTime(newValue.midnightBreakTime);
  } else {
    value.afterShiftpatternId = undefined;
    value.afterShiftpatternNm = undefined;
    value.afterStartTime = undefined;
    value.afterEndTime = undefined;
    value.afterBreakTime = undefined;
    value.afterMorningBreakTime = undefined;
    value.afterMidnightBreakTime = undefined;
  }
});

const getShiftPattern = async () => {
  const request = new PetitionShiftPatternSearchRequest({
    affiliationId: props.affiliationId
  });
  const result = await $post<ShiftPattern[]>(
    Paths.petition.search.shiftPattern,
    request
  );
  if (result) {
    // shiftPattern.value = result;
    // result.push(publicHoliday);
    shiftPattern.value = result.filter(
      e => e.shiftPatternId !== props.shift?.shiftPatternId
    );
  }
};

onMounted(async () => await getShiftPattern());

const onSubmit = async () => {
  if (!vform.value?.validate()) {
    $alert("warning", "入力に誤りがあります。\n入力内容を確認してください。");
    return;
  }
  const date = props.date.toISO();
  if (!date) {
    $alert("error", "申請に失敗しました。");
    return;
  }

  const days = new VacationDaysDatKey({
    recordId: 0,
    date: date
  });
  const dat = new VacationFlowDat({
    recordId: 0,
    employeeId: props.employeeId,
    affiliationId: props.affiliationId,
    shiftId: props.shift?.shiftId,
    requestDate: DateTime.local().toISO(),
    ...form.value,
    substituteShiftId: substitute.value?.value,
    substituteDate: formatDate(substitute.value?.text),
    preShiftPatternId: props.shift?.shiftPatternId,
    changeShiftPatternId: formShift.value.afterShiftpatternId
  });
  const request = new VacationApplicationRequest({
    vacationFlow: dat,
    vacationDays: [days]
  });
  const result = await $post(Paths.petition.insert.vacation, request);
  if (result) {
    $alert("success", "申請に成功しました。");
    return true;
  } else {
    $alert("error", "申請に失敗しました。");
    return false;
  }
};

const onClear = () => {
  vform.value?.reset();
  const value = formShift.value;
  value.afterShiftpatternId = undefined;
  value.afterShiftpatternNm = undefined;
  value.afterStartTime = undefined;
  value.afterEndTime = undefined;
  value.afterBreakTime = undefined;
  value.afterMorningBreakTime = undefined;
  value.afterMidnightBreakTime = undefined;
};

watch(segment, () => {
  vform.value?.reset();
  const value = formShift.value;
  value.afterShiftpatternId = undefined;
  value.afterShiftpatternNm = undefined;
  value.afterStartTime = undefined;
  value.afterEndTime = undefined;
  value.afterBreakTime = undefined;
  value.afterMorningBreakTime = undefined;
  value.afterMidnightBreakTime = undefined;
});

expose({
  onSubmit
});

return { __sfc: true,segmentItems, beforeHeader, afterHeader, sqlToFormatTime, segment, props, form, formShift, substitute, vform, shiftPattern, selectShift, newShiftPattern, formatDate, getShiftPattern, onSubmit, onClear, VForm, VRow, VCol, VacationForm, SubstituteHolidayFrom, BeforeAfterList }
}

})